/**
 * VueLayers
 * Web map Vue components with the power of OpenLayers
 *
 * @package vuelayers
 * @author Vladimir Vershinin <ghettovoice@gmail.com>
 * @version 0.11.37
 * @license MIT
 * @copyright (c) 2017-2021, Vladimir Vershinin <ghettovoice@gmail.com>
 */
import _asyncToGenerator from '@babel/runtime-corejs3/helpers/esm/asyncToGenerator';
import _mapInstanceProperty from '@babel/runtime-corejs3/core-js-stable/instance/map';
import _regeneratorRuntime from '@babel/runtime-corejs3/regenerator';
import { getInteractionId, getInteractionPriority, initializeInteraction, setInteractionId, setInteractionPriority } from '../ol-ext';
import { isEqual } from '../util/minilo';
import mergeDescriptors from '../util/multi-merge-descriptors';
import cmp from './ol-virt-cmp';
import useMapCmp from './use-map-cmp';

var interaction = {
  mixins: [cmp, useMapCmp],
  props: {
    active: {
      type: Boolean,
      default: true
    },

    /**
     * Priority of interactions in the event handling stream.
     * The higher the value, the sooner it will handle map event.
     * @type {number}
     */
    priority: {
      type: Number,
      default: 0
    }
  },
  methods: {
    /**
     * @return {Promise<module:ol/interaction/Interaction~Interaction>}
     * @protected
     */
    createOlObject: function createOlObject() {
      var _this = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
        var interaction;
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                _context.next = 2;
                return _this.createInteraction();

              case 2:
                interaction = _context.sent;
                initializeInteraction(interaction, _this.id, _this.priority);
                interaction.setActive(_this.active);
                return _context.abrupt("return", interaction);

              case 6:
              case "end":
                return _context.stop();
            }
          }
        }, _callee);
      }))();
    },

    /**
     * @return {module:ol/interaction/Interaction~Interaction|Promise<module:ol/interaction/Interaction~Interaction>}
     * @protected
     * @abstract
     */
    createInteraction: function createInteraction() {
      throw new Error('Not implemented method');
    },

    /**
     * @returns {Object}
     * @protected
     */
    getServices: function getServices() {
      var vm = this;
      return mergeDescriptors(cmp.methods.getServices.call(this), {
        get interaction() {
          return vm.$interaction;
        }

      });
    },

    /**
     * @return {Promise} Resolves when initialization completes
     * @protected
     */
    init: function init() {
      return cmp.methods.init.call(this);
    },

    /**
     * @return {void|Promise<void>}
     * @protected
     */
    deinit: function deinit() {
      return cmp.methods.deinit.call(this);
    },

    /**
     * @return {void}
     * @protected
     */
    mount: function mount() {
      this.$interactionsContainer && this.$interactionsContainer.addInteraction(this);
      this.subscribeAll();
    },

    /**
     * @return {void}
     * @protected
     */
    unmount: function unmount() {
      this.unsubscribeAll();
      this.$interactionsContainer && this.$interactionsContainer.removeInteraction(this);
    },

    /**
     * @return {Promise}
     */
    refresh: function refresh() {
      return cmp.methods.refresh.call(this);
    },

    /**
     * @return {Promise}
     */
    recreate: function recreate() {
      return cmp.methods.recreate.call(this);
    },

    /**
     * @return {Promise}
     */
    remount: function remount() {
      return cmp.methods.remount.call(this);
    },

    /**
     * @protected
     */
    subscribeAll: function subscribeAll() {}
  },
  watch: {
    id: function id(value) {
      if (!this.$interaction || isEqual(value, getInteractionId(this.$interaction))) {
        return;
      }

      setInteractionId(this.$interaction, value);
    },
    active: function active(value) {
      if (!this.$interaction || value === this.$interaction.getActive()) {
        return;
      }

      this.$interaction.setActive(value);
    },
    priority: function priority(value) {
      if (!this.$interaction || !this.$interactionsContainer || value === getInteractionPriority(this.$interaction)) {
        return;
      }

      setInteractionPriority(this.$interaction, value); // todo replace with event

      this.$interactionsContainer.sortInteractions();
    }
  },
  stubVNode: {
    empty: function empty() {
      return this.vmId;
    }
  },
  created: function created() {
    defineServices.call(this);
  }
};

function defineServices() {
  var _this2 = this;

  Object.defineProperties(this, {
    /**
     * @type {module:ol/interaction/Interaction~Interaction|undefined}
     */
    $interaction: {
      enumerable: true,
      get: function get() {
        return _this2.$olObject;
      }
    },
    $map: {
      enumerable: true,
      get: function get() {
        return _this2.$services && _mapInstanceProperty(_this2.$services);
      }
    },
    $view: {
      enumerable: true,
      get: function get() {
        return _this2.$services && _this2.$services.view;
      }
    },
    $interactionsContainer: {
      enumerable: true,
      get: function get() {
        return _this2.$services && _this2.$services.interactionsContainer;
      }
    }
  });
}

export default interaction;
